import { normalizeColumnConfig } from '@couplespace/core/databases';
import type { ChangeOperation } from '@couplespace/sync';
import { uuidv7 } from 'uuidv7';

import { createBlockLocal } from '../blocks/store';
import type { BlockRecord } from '../blocks/types';
import { enqueueOutbox } from '../db/outbox';
import type { LocalDatabase } from '../db/types';
import { getSyncEngine } from '../sync/engine';

import { getLocalMemberId } from './member-local';
import {
  loadPropertyLocal,
  loadSchemaLocal,
  upsertPropertyRowLocal,
  upsertSchemaRowLocal,
} from './queries';
import { syncRelationCellLocal } from './relation-sync-local';
import type {
  BlockPropertyRecord,
  DatabaseSchemaRecord,
  UpdateDatabaseSchemaInput,
} from './types';

async function getDb(): Promise<LocalDatabase> {
  const engine = getSyncEngine();
  const db = engine.getDatabase();
  if (!db) {
    await engine.start();
    const ready = engine.getDatabase();
    if (!ready) {
      throw new Error('Local database not ready');
    }
    return ready;
  }
  return db;
}

function scheduleSync(): void {
  getSyncEngine().schedulePush();
}

export async function fetchDatabaseSchemaLocal(
  databaseBlockId: string,
): Promise<DatabaseSchemaRecord> {
  const memberId = await getLocalMemberId();
  const { healDatabaseMetadataIfNeeded } = await import('./create-metadata-local');
  const schema = await healDatabaseMetadataIfNeeded(databaseBlockId, memberId);
  if (!schema) {
    throw new Error('Database schema not found locally');
  }
  return schema;
}

export async function updateDatabaseSchemaLocal(
  databaseBlockId: string,
  input: UpdateDatabaseSchemaInput,
): Promise<DatabaseSchemaRecord> {
  const db = await getDb();
  const memberId = await getLocalMemberId();
  const existing = await loadSchemaLocal(db, databaseBlockId);
  if (!existing) {
    throw new Error('Database schema not found locally');
  }
  if (
    input.expectedSyncVersion != null &&
    input.expectedSyncVersion !== existing.syncVersion
  ) {
    throw new Error('Schema version conflict');
  }

  const normalized = input.columns.map((col) => ({
    ...col,
    config: normalizeColumnConfig(col.type, col.config),
  }));

  const now = new Date().toISOString();
  const updated: DatabaseSchemaRecord = {
    ...existing,
    columns: normalized,
    schemaVersion: existing.schemaVersion + 1,
    updatedAt: now,
    syncVersion: existing.syncVersion,
  };

  await upsertSchemaRowLocal(db, {
    ...updated,
    updatedBy: memberId,
    actorKind: 'human',
    vectorClock: {},
  });

  await enqueueOutbox(db, {
    tableName: 'database_schemas',
    operation: 'update',
    row: {
      id: updated.id,
      databaseId: updated.databaseId,
      columns: updated.columns,
      schemaVersion: updated.schemaVersion,
      updatedAt: now,
      updatedBy: memberId,
      actorKind: 'human',
      vectorClock: {},
      syncVersion: updated.syncVersion,
    },
    baseSyncVersion: existing.syncVersion,
  });

  scheduleSync();
  return updated;
}

export async function createDatabaseRowLocal(
  databaseBlockId: string,
  input?: { title?: string; position?: number },
): Promise<BlockRecord> {
  const memberId = await getLocalMemberId();
  const db = await getDb();
  const { listRowBlocksLocal } = await import('./queries');
  const rows = await listRowBlocksLocal(db, databaseBlockId);
  const maxPos = rows.reduce((m, r) => Math.max(m, r.position), -1);
  return createBlockLocal({
    id: uuidv7(),
    parentId: databaseBlockId,
    type: 'page',
    title: input?.title ?? 'Untitled',
    position: input?.position ?? maxPos + 1,
    createdBy: memberId,
  });
}

export async function upsertRowPropertyLocal(
  rowId: string,
  propertyId: string,
  value: Record<string, unknown>,
  options?: { expectedSyncVersion?: number },
): Promise<BlockPropertyRecord> {
  const db = await getDb();
  const memberId = await getLocalMemberId();
  const existing = await loadPropertyLocal(db, rowId, propertyId);
  const prevValue = existing?.value ?? null;

  if (
    options?.expectedSyncVersion != null &&
    existing &&
    options.expectedSyncVersion !== existing.syncVersion
  ) {
    throw new Error('Property version conflict');
  }

  const now = new Date().toISOString();
  let prop: BlockPropertyRecord;
  let operation: ChangeOperation;
  let baseVersion: number | null = null;

  if (existing) {
    prop = {
      ...existing,
      value,
      syncVersion: existing.syncVersion,
    };
    operation = 'update';
    baseVersion = existing.syncVersion;
    await upsertPropertyRowLocal(db, {
      ...prop,
      updatedAt: now,
      updatedBy: memberId,
      actorKind: 'human',
      vectorClock: {},
    });
  } else {
    prop = {
      id: uuidv7(),
      blockId: rowId,
      propertyId,
      value,
      syncVersion: 1,
    };
    operation = 'insert';
    await upsertPropertyRowLocal(db, {
      ...prop,
      createdAt: now,
      updatedAt: now,
      updatedBy: memberId,
      actorKind: 'human',
      vectorClock: {},
    });
  }

  await enqueueOutbox(db, {
    tableName: 'block_properties',
    operation,
    row: {
      id: prop.id,
      blockId: prop.blockId,
      propertyId: prop.propertyId,
      value: prop.value,
      createdAt: now,
      updatedAt: now,
      updatedBy: memberId,
      actorKind: 'human',
      vectorClock: {},
      syncVersion: prop.syncVersion,
    },
    baseSyncVersion: baseVersion,
  });

  const schema = await loadSchemaLocal(
    db,
    (
      await db.getFirstAsync<{ parent_id: string | null }>(
        `SELECT parent_id FROM blocks WHERE id = ?`,
        rowId,
      )
    )?.parent_id ?? '',
  );
  const col = schema?.columns.find((c) => c.id === propertyId);
  if (col?.type === 'relation') {
    await syncRelationCellLocal(db, {
      sourceRowId: rowId,
      propertyId,
      prevValue,
      nextValue: value,
    });
  }

  scheduleSync();
  return prop;
}

export async function resolveBlockTitlesLocal(
  ids: string[],
): Promise<Record<string, string>> {
  const db = await getDb();
  const { queryBlocksByIdsLocal } = await import('./queries');
  const blocks = await queryBlocksByIdsLocal(db, ids);
  const out: Record<string, string> = {};
  for (const b of blocks) {
    out[b.id] = b.title ?? 'Untitled';
  }
  return out;
}
